// AGC: Tour Overlay component - wraps react-joyride with custom styling (v6.9.0)
// AGC: Features: Custom tooltip, screen dimming, spotlight, hand pointer

import React, { useCallback, useEffect, useState } from 'react'
import Joyride, { CallBackProps, EVENTS, STATUS, ACTIONS } from 'react-joyride'
import { useTourGuide } from '../../providers/TourGuideProvider'
import { getTourById } from '../../config/tourSteps'
import { TourTooltip } from './TourTooltip'
import { BattleModeSelectionModal } from '../modals/BattleModeSelectionModal'

// AGC: Custom joyride styles with yellow flashing highlights (v6.9.0)
const joyrideStyles = {
  options: {
    arrowColor: 'transparent', // AGC: Hide default arrow, using hand pointer instead
    backgroundColor: 'transparent',
    overlayColor: 'rgba(0, 0, 0, 0.85)',
    primaryColor: '#eab308', // yellow-500
    spotlightShadow: '0 0 40px rgba(234, 179, 8, 0.8)', // AGC: Yellow glow
    textColor: '#fff',
    width: undefined,
    zIndex: 10000,
  },
  spotlight: {
    borderRadius: '16px',
    // AGC: Yellow flashing highlight - animated box shadow (applied via CSS animation)
    boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.85), 0 0 0 4px rgba(234, 179, 8, 1), 0 0 40px rgba(234, 179, 8, 0.8)',
  },
  overlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.85)',
  },
  tooltip: {
    borderRadius: '16px',
    padding: 0,
    // AGC: CRITICAL - No yellow glow on tooltip, use neutral shadow only
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  },
  tooltipContainer: {
    textAlign: 'left' as const,
  },
  buttonNext: {
    display: 'none', // AGC: Using custom buttons
  },
  buttonBack: {
    display: 'none', // AGC: Using custom buttons
  },
  buttonSkip: {
    display: 'none', // AGC: Using custom buttons
  },
  buttonClose: {
    display: 'none', // AGC: Using custom buttons
  },
}

// AGC: Tour Overlay component
export const TourOverlay: React.FC = () => {
  const { 
    state, 
    handleJoyrideCallback,
    getTotalSteps,
    nextStep,
  } = useTourGuide()
  
  const { isRunning, activeTourId, currentStepIndex, isAutoAdvancing } = state
  
  // AGC: Get current tour steps
  const currentTour = activeTourId ? getTourById(activeTourId) : null
  const steps = currentTour?.steps ?? []
  
  // AGC: Battle mode selection modal state (for step 13)
  const [showModeSelection, setShowModeSelection] = useState(false)
  
  // AGC: Custom tooltip renderer
  const renderTooltip = useCallback((props: any) => {
    return (
      <TourTooltip
        {...props}
        totalSteps={getTotalSteps()}
        currentStep={currentStepIndex}
        isAutoAdvancing={isAutoAdvancing}
      />
    )
  }, [getTotalSteps, currentStepIndex, isAutoAdvancing])

  // AGC: CRITICAL - Remove yellow glow from tooltip elements (runs on every step change + MutationObserver)
  useEffect(() => {
    if (!isRunning) return
    
    // AGC: Force remove yellow glow from all tooltip elements
    const removeTooltipGlow = () => {
      const tooltips = document.querySelectorAll('[class*="react-joyride__tooltip"], [class*="tooltip"]:not([class*="spotlight"])')
      tooltips.forEach((tooltip) => {
        const element = tooltip as HTMLElement
        element.style.animation = 'none !important'
        element.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06) !important'
        // AGC: Remove any yellow colors from computed styles
        const computedStyle = window.getComputedStyle(element)
        if (computedStyle.boxShadow.includes('234, 179, 8') || computedStyle.boxShadow.includes('eab308')) {
          element.style.setProperty('box-shadow', '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)', 'important')
        }
        // AGC: Remove from all children too
        const children = element.querySelectorAll('*')
        children.forEach((child) => {
          const childEl = child as HTMLElement
          childEl.style.animation = 'none !important'
          childEl.style.boxShadow = 'none !important'
        })
      })
    }
    
    // AGC: Run immediately and on intervals to catch dynamically created elements
    removeTooltipGlow()
    const timer = setInterval(removeTooltipGlow, 100) // AGC: Check every 100ms
    
    // AGC: Use MutationObserver to catch tooltips as they're created
    const observer = new MutationObserver(() => {
      removeTooltipGlow()
    })
    
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style'],
    })
    
    return () => {
      clearInterval(timer)
      observer.disconnect()
    }
  }, [isRunning, currentStepIndex])

  // AGC: Handle step 1 (index 0) - Adjust spotlight cutout position: 2px before top, left, right edges, reduce 10px from bottom
  useEffect(() => {
    if (!isRunning || activeTourId !== 'navigation' || currentStepIndex !== 0) return

    const adjustSpotlight = () => {
      const spotlight = document.querySelector('div.react-joyride__spotlight') as HTMLElement | null
      if (spotlight) {
        const rect = spotlight.getBoundingClientRect()
        const computedStyle = window.getComputedStyle(spotlight)
        const currentTop = parseFloat(computedStyle.top) || rect.top
        const currentLeft = parseFloat(computedStyle.left) || rect.left
        const currentWidth = parseFloat(computedStyle.width) || rect.width
        const currentHeight = parseFloat(computedStyle.height) || rect.height
        
        // AGC: Position cutout 2px before top, left, and right edges
        // Adjust top position: move down 2px from top edge
        if (currentTop < 2) {
          spotlight.style.top = '2px'
        }
        // Adjust left position: move right 2px from left edge
        if (currentLeft < 2) {
          spotlight.style.left = '2px'
        }
        // Adjust width: reduce by 4px (2px left + 2px right) to account for margins
        const viewportWidth = window.innerWidth
        if (currentLeft + currentWidth > viewportWidth - 2) {
          spotlight.style.width = `${viewportWidth - parseFloat(spotlight.style.left || '0') - 2}px`
        } else {
          spotlight.style.width = `${currentWidth - 4}px`
        }
        // AGC: Reduce height by 10px from bottom
        spotlight.style.height = `${currentHeight - 10}px`
        spotlight.style.transition = 'all 0.3s ease'
      }
    }
    
    // AGC: Run after a delay to ensure spotlight is rendered
    const timer = setTimeout(adjustSpotlight, 100)
    
    return () => {
      clearTimeout(timer)
      const spotlight = document.querySelector('div.react-joyride__spotlight') as HTMLElement | null
      if (spotlight) {
        spotlight.style.top = ''
        spotlight.style.left = ''
        spotlight.style.width = ''
        spotlight.style.height = ''
        spotlight.style.transition = ''
      }
    }
  }, [isRunning, activeTourId, currentStepIndex])

  // AGC: Handle step 8 (index 7) - Adjust spotlight cutout position: 2px before bottom, left, right edges, reduce 10px from top
  useEffect(() => {
    if (!isRunning || activeTourId !== 'navigation' || currentStepIndex !== 7) return

    const adjustSpotlight = () => {
      const spotlight = document.querySelector('div.react-joyride__spotlight') as HTMLElement | null
      if (spotlight) {
        const rect = spotlight.getBoundingClientRect()
        const computedStyle = window.getComputedStyle(spotlight)
        const currentTop = parseFloat(computedStyle.top) || rect.top
        const currentLeft = parseFloat(computedStyle.left) || rect.left
        const currentWidth = parseFloat(computedStyle.width) || rect.width
        const currentHeight = parseFloat(computedStyle.height) || rect.height
        const viewportWidth = window.innerWidth
        const viewportHeight = window.innerHeight
        
        // AGC: Position cutout 2px before bottom, left, and right edges
        // Adjust left position: move right 2px from left edge
        if (currentLeft < 2) {
          spotlight.style.left = '2px'
        }
        // Adjust width: reduce by 4px (2px left + 2px right) to account for margins
        if (currentLeft + currentWidth > viewportWidth - 2) {
          spotlight.style.width = `${viewportWidth - parseFloat(spotlight.style.left || '0') - 2}px`
        } else {
          spotlight.style.width = `${currentWidth - 4}px`
        }
        // Adjust bottom position: move up 2px from bottom edge
        if (currentTop + currentHeight > viewportHeight - 2) {
          spotlight.style.top = `${viewportHeight - currentHeight - 2}px`
        }
        // AGC: Reduce height by 10px from top (move down 10px and reduce height)
        // AGC: Also move up 3px (reduce top by 3px)
        spotlight.style.top = `${currentTop + 10 - 3}px`
        spotlight.style.height = `${currentHeight - 10}px`
        spotlight.style.transition = 'all 0.3s ease'
      }
    }
    
    // AGC: Run after a delay to ensure spotlight is rendered
    const timer = setTimeout(adjustSpotlight, 100)
    
    return () => {
      clearTimeout(timer)
      const spotlight = document.querySelector('div.react-joyride__spotlight') as HTMLElement | null
      if (spotlight) {
        spotlight.style.top = ''
        spotlight.style.left = ''
        spotlight.style.width = ''
        spotlight.style.height = ''
        spotlight.style.transition = ''
      }
    }
  }, [isRunning, activeTourId, currentStepIndex])

  // AGC: Handle step 2 (index 1) - Add 10% height visibility + yellow glow spotlight
  useEffect(() => {
    if (!isRunning || activeTourId !== 'navigation' || currentStepIndex !== 1) return

    const networkButton = document.querySelector('[data-tour="network-button"]') as HTMLElement | null
    if (networkButton) {
      const originalPadding = networkButton.style.padding || ''
      const originalPaddingTop = networkButton.style.paddingTop || ''
      const originalPaddingBottom = networkButton.style.paddingBottom || ''
      const originalTransition = networkButton.style.transition || ''
      
      // AGC: Get natural height and add 10%
      const rect = networkButton.getBoundingClientRect()
      const naturalHeight = rect.height
      const heightIncrease = naturalHeight * 0.1
      const paddingTop = Math.ceil(heightIncrease / 2)
      const paddingBottom = Math.ceil(heightIncrease / 2)
      
      networkButton.style.paddingTop = `${paddingTop}px`
      networkButton.style.paddingBottom = `${paddingBottom}px`
      networkButton.style.transition = 'all 0.3s ease'
      
      return () => {
        if (networkButton) {
          networkButton.style.padding = originalPadding
          networkButton.style.paddingTop = originalPaddingTop
          networkButton.style.paddingBottom = originalPaddingBottom
          networkButton.style.transition = originalTransition
        }
      }
    }
  }, [isRunning, activeTourId, currentStepIndex])

  // AGC: Handle step 3 (index 2) - Reduce spotlight cutout by 3px from top
  useEffect(() => {
    if (!isRunning || activeTourId !== 'navigation' || currentStepIndex !== 2) return

    const adjustSpotlight = () => {
      const spotlight = document.querySelector('div.react-joyride__spotlight') as HTMLElement | null
      if (spotlight) {
        const rect = spotlight.getBoundingClientRect()
        const computedStyle = window.getComputedStyle(spotlight)
        const currentTop = parseFloat(computedStyle.top) || rect.top
        const currentHeight = parseFloat(computedStyle.height) || rect.height
        
        // AGC: Reduce cutout by 3px from top (move top down 3px and reduce height by 3px)
        spotlight.style.top = `${currentTop + 3}px`
        spotlight.style.height = `${currentHeight - 3}px`
        spotlight.style.transition = 'all 0.3s ease'
      }
    }
    
    // AGC: Run after a delay to ensure spotlight is rendered
    const timer = setTimeout(adjustSpotlight, 100)
    
    return () => {
      clearTimeout(timer)
      const spotlight = document.querySelector('div.react-joyride__spotlight') as HTMLElement | null
      if (spotlight) {
        spotlight.style.top = ''
        spotlight.style.height = ''
        spotlight.style.transition = ''
      }
    }
  }, [isRunning, activeTourId, currentStepIndex])

  // AGC: Handle network dropdown and Base Mainnet highlight for navigation tour step 3 (index 2)
  useEffect(() => {
    if (!isRunning || activeTourId !== 'navigation' || currentStepIndex !== 2) return

    const wrapper = document.querySelector('[data-tour="network-server-area"]') as HTMLDivElement | null
    if (!wrapper) return
    
    // AGC: Store original styles - TopNav sets paddingBottom: '120px' and marginBottom: '-120px' via React inline styles
    // AGC: Read from the actual style object (React manages these as inline styles)
    const originalPadding = wrapper.style.padding || ''
    const originalPaddingBottom = wrapper.style.paddingBottom || '120px' // AGC: TopNav's default value
    const originalMarginBottom = wrapper.style.marginBottom || '-120px' // AGC: TopNav's default value
    const originalMinHeight = wrapper.style.minHeight || ''
    const originalWidth = wrapper.style.width || ''
    const originalTransition = wrapper.style.transition || ''

    // AGC: Reduced width by 30% (256px -> 179px) and height by 50% (220px -> 110px) + yellow glow spotlight
    wrapper.style.padding = '18px 34px 110px 34px'
    wrapper.style.marginBottom = '-90px'
    wrapper.style.minHeight = '110px'
    wrapper.style.width = '179px' // AGC: 30% reduction from 256px (256 * 0.7 = 179.2)
    wrapper.style.transition = 'all 0.3s ease'

    let timer1: NodeJS.Timeout | null = null
    let timer2: NodeJS.Timeout | null = null
    let flashInterval: NodeJS.Timeout | null = null
    let networkButton: HTMLButtonElement | null = null
    let mainnetButton: HTMLButtonElement | null = null

    timer1 = setTimeout(() => {
      networkButton = document.querySelector('[data-tour="network-button"]') as HTMLButtonElement | null
      if (networkButton && !networkButton.disabled) {
        networkButton.click()

        timer2 = setTimeout(() => {
          mainnetButton = document.querySelector('[data-tour="network-mainnet"]') as HTMLButtonElement | null
          if (mainnetButton) {
            mainnetButton.style.transition = 'all 0.3s ease'
            mainnetButton.style.boxShadow = '0 0 0 3px rgba(234, 179, 8, 1), 0 0 22px rgba(234, 179, 8, 0.9)'
            mainnetButton.style.borderRadius = '10px'

            let isBright = false
            flashInterval = setInterval(() => {
              if (mainnetButton) {
                isBright = !isBright
                mainnetButton.style.boxShadow = isBright
                  ? '0 0 0 4px rgba(234, 179, 8, 1), 0 0 30px rgba(234, 179, 8, 1)'
                  : '0 0 0 3px rgba(234, 179, 8, 1), 0 0 22px rgba(234, 179, 8, 0.9)'
              }
            }, 600)
          }
        }, 400)
      }
    }, 400)

    return () => {
      if (timer1) clearTimeout(timer1)
      if (timer2) clearTimeout(timer2)
      if (flashInterval) clearInterval(flashInterval)
      if (mainnetButton) {
        mainnetButton.style.boxShadow = ''
        mainnetButton.style.borderRadius = ''
        mainnetButton.style.transition = ''
      }
      // AGC: Close dropdown when leaving step 3
      if (networkButton && !networkButton.disabled) {
        // AGC: Check if dropdown is open before closing
        const dropdown = document.querySelector('[data-tour="network-mainnet"]')?.closest('div')
        if (dropdown && dropdown.style.display !== 'none') {
          networkButton.click()
        }
      }
      // AGC: CRITICAL - Restore all wrapper styles to prevent button movement when going back to step 2
      // AGC: Must restore to TopNav's original inline styles: paddingBottom: '120px', marginBottom: '-120px'
      if (wrapper) {
        wrapper.style.padding = ''
        wrapper.style.paddingBottom = '120px' // AGC: Restore TopNav's original value
        wrapper.style.marginBottom = '-120px' // AGC: Restore TopNav's original value
        wrapper.style.minHeight = ''
        wrapper.style.width = ''
        wrapper.style.transition = ''
      }
    }
  }, [isRunning, activeTourId, currentStepIndex])

  // AGC: Cleanup wrapper styles when NOT on step 3 (index 2) - ensures proper reset when going back to step 2
  useEffect(() => {
    if (!isRunning || activeTourId !== 'navigation' || currentStepIndex === 2) return

    // AGC: If we're not on step 3, ensure wrapper is reset to TopNav's original styles
    const wrapper = document.querySelector('[data-tour="network-server-area"]') as HTMLDivElement | null
    if (wrapper) {
      // AGC: Restore to TopNav's original inline styles
      wrapper.style.padding = ''
      wrapper.style.paddingBottom = '120px' // AGC: TopNav's original value
      wrapper.style.marginBottom = '-120px' // AGC: TopNav's original value
      wrapper.style.minHeight = ''
      wrapper.style.width = ''
      wrapper.style.transition = ''
    }
  }, [isRunning, activeTourId, currentStepIndex])

  // AGC: Handle step 4 (index 3) - Add 7% width and 7% height visibility + yellow glow spotlight
  useEffect(() => {
    if (!isRunning || activeTourId !== 'navigation' || currentStepIndex !== 3) return

    // AGC: Force cleanup of any remaining wrapper styles from step 3
    const wrapper = document.querySelector('[data-tour="network-server-area"]') as HTMLDivElement | null
    if (wrapper) {
      // AGC: Reset all styles to ensure button doesn't move - restore TopNav's original values
      wrapper.style.padding = ''
      wrapper.style.paddingBottom = '120px' // AGC: Restore TopNav's original value
      wrapper.style.marginBottom = '-120px' // AGC: Restore TopNav's original value
      wrapper.style.minHeight = ''
      wrapper.style.width = ''
      wrapper.style.transition = ''
    }

    // AGC: Add 7% width and 7% height visibility + yellow glow spotlight to server status
    const serverStatus = document.querySelector('[data-tour="server-status"]') as HTMLElement | null
    if (serverStatus) {
      const originalPadding = serverStatus.style.padding || ''
      const originalPaddingTop = serverStatus.style.paddingTop || ''
      const originalPaddingBottom = serverStatus.style.paddingBottom || ''
      const originalPaddingLeft = serverStatus.style.paddingLeft || ''
      const originalPaddingRight = serverStatus.style.paddingRight || ''
      const originalTransition = serverStatus.style.transition || ''
      
      // AGC: Get natural dimensions and add 7%
      const rect = serverStatus.getBoundingClientRect()
      const naturalWidth = rect.width
      const naturalHeight = rect.height
      const widthIncrease = naturalWidth * 0.07
      const heightIncrease = naturalHeight * 0.07
      const paddingHorizontal = Math.ceil(widthIncrease / 2)
      const paddingVertical = Math.ceil(heightIncrease / 2)
      
      serverStatus.style.paddingTop = `${paddingVertical}px`
      serverStatus.style.paddingBottom = `${paddingVertical}px`
      serverStatus.style.paddingLeft = `${paddingHorizontal}px`
      serverStatus.style.paddingRight = `${paddingHorizontal}px`
      serverStatus.style.transition = 'all 0.3s ease'
      
      return () => {
        if (serverStatus) {
          serverStatus.style.padding = originalPadding
          serverStatus.style.paddingTop = originalPaddingTop
          serverStatus.style.paddingBottom = originalPaddingBottom
          serverStatus.style.paddingLeft = originalPaddingLeft
          serverStatus.style.paddingRight = originalPaddingRight
          serverStatus.style.transition = originalTransition
        }
      }
    }
  }, [isRunning, activeTourId, currentStepIndex])

  // AGC: Handle step 14 (index 13) - Scroll to top of page
  useEffect(() => {
    if (!isRunning || activeTourId !== 'navigation' || currentStepIndex !== 13) return

    // AGC: Scroll to top smoothly
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [isRunning, activeTourId, currentStepIndex])

  // AGC: Handle step 15 (index 14) - Scroll to top of page
  useEffect(() => {
    if (!isRunning || activeTourId !== 'navigation' || currentStepIndex !== 14) return

    // AGC: Scroll to top smoothly
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [isRunning, activeTourId, currentStepIndex])

  // AGC: Handle step 17 (index 16) - Add 5px extra cutout on bottom
  useEffect(() => {
    if (!isRunning || activeTourId !== 'navigation' || currentStepIndex !== 16) return

    const adjustSpotlight = () => {
      const spotlight = document.querySelector('div.react-joyride__spotlight') as HTMLElement | null
      if (spotlight) {
        const rect = spotlight.getBoundingClientRect()
        const computedStyle = window.getComputedStyle(spotlight)
        const currentHeight = parseFloat(computedStyle.height) || rect.height
        
        // AGC: Add 5px to height (increases cutout from bottom)
        spotlight.style.height = `${currentHeight + 5}px`
        spotlight.style.transition = 'all 0.3s ease'
      }
    }
    
    // AGC: Run after a delay to ensure spotlight is rendered
    const timer = setTimeout(adjustSpotlight, 100)
    
    return () => {
      clearTimeout(timer)
      const spotlight = document.querySelector('div.react-joyride__spotlight') as HTMLElement | null
      if (spotlight) {
        spotlight.style.height = ''
        spotlight.style.transition = ''
      }
    }
  }, [isRunning, activeTourId, currentStepIndex])

  // AGC: Handle step 6 (index 5) - Navigate to quickBattle page and highlight player progression
  useEffect(() => {
    if (!isRunning || activeTourId !== 'battle' || currentStepIndex !== 5) return
    
    console.log('AGC: Step 6 (index 5) started - navigating to quickBattle page')
    
    // AGC: Navigate to quickBattle page when step 6 starts
    window.dispatchEvent(new CustomEvent('tour:navigate-to-page', { 
      detail: { page: 'quickBattle' } 
    }))
    
    // AGC: After navigation, manually highlight the player progression element
    const highlightElement = () => {
      const step = steps[currentStepIndex]
      const highlightTarget = (step as any)?.data?.highlightTarget
      console.log('AGC: Step 6 - attempting to highlight:', highlightTarget)
      if (!highlightTarget) return
      
      const targetElement = document.querySelector(highlightTarget) as HTMLElement | null
      if (targetElement) {
        console.log('AGC: Step 6 - found target element, applying highlight')
        // AGC: Add a custom highlight class
        targetElement.style.position = 'relative'
        targetElement.style.zIndex = '10001'
        targetElement.style.boxShadow = '0 0 0 4px rgba(234, 179, 8, 1), 0 0 40px rgba(234, 179, 8, 0.8)'
        targetElement.style.borderRadius = '16px'
        targetElement.style.animation = 'yellowFlashSpotlight 1.5s ease-in-out infinite'
      } else {
        console.log('AGC: Step 6 - target element not found yet, retrying...')
        // AGC: Retry a few times in case page is still loading
        setTimeout(highlightElement, 500)
      }
    }
    
    // AGC: Wait for navigation to complete, then highlight
    const timer = setTimeout(highlightElement, 800)
    
    return () => {
      clearTimeout(timer)
      // AGC: Remove highlight on cleanup
      const step = steps[currentStepIndex]
      const highlightTarget = (step as any)?.data?.highlightTarget
      if (highlightTarget) {
        const targetElement = document.querySelector(highlightTarget) as HTMLElement | null
        if (targetElement) {
          targetElement.style.position = ''
          targetElement.style.zIndex = ''
          targetElement.style.boxShadow = ''
          targetElement.style.borderRadius = ''
          targetElement.style.animation = ''
        }
      }
    }
  }, [isRunning, activeTourId, currentStepIndex, steps])
  
  // AGC: Step 5 (index 4) - Battle Tour: Works like any other step
  // AGC: Step 6 (index 5) uses body as target, navigation and highlighting handled above

  // AGC: Step 5 - Auto-advance to step 6 after navigation to prevent "Target not mounted" error

  // AGC: Handle step 7 (index 6) - Battle Tour: Simulate click on normal difficulty button
  useEffect(() => {
    if (!isRunning || activeTourId !== 'battle' || currentStepIndex !== 6) return

    const simulateDifficultyClick = () => {
      const normalButton = document.querySelector('[data-tour="battle-difficulty-normal"]') as HTMLButtonElement | null
      if (normalButton && !normalButton.disabled) {
        // AGC: Simulate click without hand pointer
        normalButton.style.transition = 'all 0.2s ease'
        normalButton.style.transform = 'scale(0.95)'
        
        setTimeout(() => {
          normalButton.style.transform = ''
          normalButton.click()
        }, 300)
      }
    }
    
    const timer = setTimeout(simulateDifficultyClick, 1000)
    
    return () => {
      clearTimeout(timer)
    }
  }, [isRunning, activeTourId, currentStepIndex])

  // AGC: Step 10 (index 9) - No special handling needed, just shows the buff panels explanation (hideHand: true)
  // AGC: Step 11 (index 10) - No special handling needed, just shows the buff selection explanation (hideHand: true)

  // AGC: Handle step 12 (index 11) - Battle Tour: Click POTIONS tab
  useEffect(() => {
    if (!isRunning || activeTourId !== 'battle' || currentStepIndex !== 11) return
    
    console.log('AGC: Step 12 (index 11) - Clicking POTIONS tab')
    
    // AGC: Click POTIONS tab to show potions
    const clickPotionsTab = () => {
      const potionsTab = document.querySelector('[data-tour="battle-potions-tab"]') as HTMLButtonElement | null
      if (potionsTab) {
        console.log('AGC: Step 12 - Clicking POTIONS tab')
        potionsTab.click()
      } else {
        console.log('AGC: Step 12 - POTIONS tab not found, retrying...')
        setTimeout(clickPotionsTab, 500)
      }
    }
    
    // AGC: Click POTIONS tab after a short delay to ensure tab is ready
    const timer = setTimeout(clickPotionsTab, 300)
    
    return () => {
      clearTimeout(timer)
    }
  }, [isRunning, activeTourId, currentStepIndex])

  // AGC: Handle step 13 (index 12) - Battle Tour: Simulate click on top-left potion
  useEffect(() => {
    if (!isRunning || activeTourId !== 'battle' || currentStepIndex !== 12) return
    
    console.log('AGC: Step 13 (index 12) - Simulating click on top-left potion')
    
    // AGC: Wait for potions area to be visible, then simulate click on top-left potion
    const simulatePotionClick = () => {
      const topLeftPotion = document.querySelector('[data-tour="battle-potion-top-left"]') as HTMLButtonElement | null
      if (topLeftPotion && !topLeftPotion.disabled) {
        console.log('AGC: Step 13 - Clicking top-left potion')
        topLeftPotion.click()
      } else {
        console.log('AGC: Step 13 - Top-left potion not found or disabled, retrying...')
        setTimeout(simulatePotionClick, 500)
      }
    }
    
    // AGC: Wait a bit for POTIONS tab to switch and potions to load
    const timer = setTimeout(simulatePotionClick, 800)
    
    return () => {
      clearTimeout(timer)
    }
  }, [isRunning, activeTourId, currentStepIndex])

  // AGC: Handle step 14 (index 13) - Battle Tour: Highlight Start Battle button
  useEffect(() => {
    if (!isRunning || activeTourId !== 'battle' || currentStepIndex !== 13) return
    
    console.log('AGC: Step 14 (index 13) - Highlighting Start Battle button')
    
    // AGC: After a delay, manually highlight the Start Battle button
    const highlightButton = () => {
      const step = steps[currentStepIndex]
      const highlightTarget = (step as any)?.data?.highlightTarget
      if (!highlightTarget) return
      
      const targetElement = document.querySelector(highlightTarget) as HTMLElement | null
      if (targetElement) {
        console.log('AGC: Step 14 - Found Start Battle button, applying highlight')
        // AGC: Add highlight styles
        targetElement.style.position = 'relative'
        targetElement.style.zIndex = '10001'
        targetElement.style.boxShadow = '0 0 0 4px rgba(234, 179, 8, 1), 0 0 40px rgba(234, 179, 8, 0.8)'
        targetElement.style.borderRadius = '16px'
        targetElement.style.animation = 'yellowFlashSpotlight 1.5s ease-in-out infinite'
      } else {
        console.log('AGC: Step 14 - Start Battle button not found yet, retrying...')
        // AGC: Retry a few times in case button is still loading
        setTimeout(highlightButton, 500)
      }
    }
    
    // AGC: Wait for button to be available, then highlight
    const timer = setTimeout(highlightButton, 800)
    
    return () => {
      clearTimeout(timer)
      // AGC: Remove highlight on cleanup
      const step = steps[currentStepIndex]
      const highlightTarget = (step as any)?.data?.highlightTarget
      if (highlightTarget) {
        const targetElement = document.querySelector(highlightTarget) as HTMLElement | null
        if (targetElement) {
          targetElement.style.position = ''
          targetElement.style.zIndex = ''
          targetElement.style.boxShadow = ''
          targetElement.style.borderRadius = ''
          targetElement.style.animation = ''
        }
      }
    }
  }, [isRunning, activeTourId, currentStepIndex, steps])

  // AGC: Handle step 14 (index 13) - Battle Tour: Show mode selection modal
  useEffect(() => {
    if (!isRunning || activeTourId !== 'battle' || currentStepIndex !== 13) {
      setShowModeSelection(false)
      return
    }

    console.log('AGC: Step 14 (index 13) - Showing mode selection modal')
    const step = steps[currentStepIndex]
    if ((step as any)?.data?.showModeSelection) {
      setShowModeSelection(true)
    }
  }, [isRunning, activeTourId, currentStepIndex, steps])

  // AGC: Handle step 15 (index 14) - Battle Tour: Wait for battle to start (OLD - now step 15 is navigation)
  // AGC: This step is now handled by the mode selection modal

  // AGC: Handle step 15 (index 15) - Battle Tour: Navigate to simulation page
  useEffect(() => {
    if (!isRunning || activeTourId !== 'battle' || currentStepIndex !== 15) return

    console.log('AGC: Step 15 (index 15) - Navigating to simulation page')
    
    const step = steps[currentStepIndex]
    const navigateTo = (step as any)?.data?.navigateTo
    
    if (navigateTo === 'newSimulation') {
      // AGC: Navigate to newSimulation page
      window.dispatchEvent(new CustomEvent('tour:navigate-to-page', { 
        detail: { page: 'newSimulation' } 
      }))
    }
  }, [isRunning, activeTourId, currentStepIndex, steps])

  // AGC: Handle step 16 (index 15) - Battle Tour: Scroll to top for simulation info panel
  useEffect(() => {
    if (!isRunning || activeTourId !== 'battle' || currentStepIndex !== 15) return

    console.log('AGC: Step 16 (index 15) - Simulation info panel, scrolling to top')
    
    const step = steps[currentStepIndex]
    const scrollToTop = (step as any)?.data?.scrollToTop
    
    // AGC: Scroll to top like navigation tour
    if (scrollToTop) {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }, [isRunning, activeTourId, currentStepIndex, steps])

  // AGC: FIX for first-click-ignored issue on steps 16-19, 25-26
  // AGC: Disable pointer-events on the Joyride overlay to allow clicks to pass through immediately
  // AGC: The spotlight area already has pointer-events: auto, but the overlay captures first click
  useEffect(() => {
    if (!isRunning || activeTourId !== 'battle') return
    
    // AGC: Steps 16-19, 25-27 (indices) need click-through overlay
    const stepsNeedingClickThrough = [16, 17, 18, 19, 25, 26, 27]
    const needsClickThrough = stepsNeedingClickThrough.includes(currentStepIndex)
    
    const setOverlayPointerEvents = (enabled: boolean) => {
      const overlay = document.querySelector('.react-joyride__overlay') as HTMLElement | null
      if (overlay) {
        overlay.style.pointerEvents = enabled ? 'none' : 'auto'
        console.log(`AGC: Overlay pointer-events set to ${enabled ? 'none' : 'auto'} for step ${currentStepIndex + 1}`)
      }
    }
    
    if (needsClickThrough) {
      // AGC: Disable overlay click capture after a short delay (let Joyride set up first)
      const timer = setTimeout(() => {
        setOverlayPointerEvents(true)
      }, 100)
      
      return () => {
        clearTimeout(timer)
        setOverlayPointerEvents(false)
      }
    }
  }, [isRunning, activeTourId, currentStepIndex])

  // AGC: Handle step 17 (index 16) - Battle Tour: Wait for 2 buffs to be selected, scroll to top
  // AGC: spotlightClicks={true} and disableOverlayClose={true} in Joyride config allow buff clicks without closing
  useEffect(() => {
    if (!isRunning || activeTourId !== 'battle' || currentStepIndex !== 16) return

    console.log('AGC: Step 17 (index 16) - Scrolling to top and waiting for 2 buffs')
    
    const step = steps[currentStepIndex]
    const waitForBuffs = (step as any)?.data?.waitForBuffsSelected
    const scrollToTop = (step as any)?.data?.scrollToTop
    
    // AGC: Scroll to top immediately on step entry
    if (scrollToTop) {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
    
    if (waitForBuffs === 2) {
      // AGC: Check for selected buffs (they appear as selected in the UI)
      const checkBuffsSelected = setInterval(() => {
        // AGC: Check if buffs are selected by looking for selected state in BuffSelector
        const buffPanels = document.querySelectorAll('[data-tour="simulation-buff-area"] [data-buff-id]')
        let selectedCount = 0
        
        buffPanels.forEach((panel) => {
          const isSelected = panel.classList.contains('ring-2') || 
                           panel.classList.contains('ring-yellow-400') ||
                           panel.classList.contains('ring-cyan-400') ||
                           panel.getAttribute('data-selected') === 'true'
          if (isSelected) selectedCount++
        })
        
        // AGC: Also check by looking for selected buff indicators
        const selectedBuffs = document.querySelectorAll('[data-tour="simulation-buff-area"] .bg-yellow-500, [data-tour="simulation-buff-area"] .ring-yellow-400, [data-tour="simulation-buff-area"] .ring-cyan-400')
        if (selectedBuffs.length >= 2 || selectedCount >= 2) {
          console.log('AGC: 2 buffs selected, advancing tour...')
          clearInterval(checkBuffsSelected)
      setTimeout(() => {
        nextStep()
          }, 500)
        }
      }, 500)

      return () => {
        clearInterval(checkBuffsSelected)
      }
    }
  }, [isRunning, activeTourId, currentStepIndex, steps, nextStep])

  // AGC: Handle step 18 (index 17) - Battle Tour: Simulate click on potions tab
  useEffect(() => {
    if (!isRunning || activeTourId !== 'battle' || currentStepIndex !== 17) return

    console.log('AGC: Step 18 (index 17) - Simulating click on potions tab')
    
    const step = steps[currentStepIndex]
    const simulateClick = (step as any)?.data?.simulateClick
    const clickTarget = (step as any)?.data?.clickTarget
    const waitForTabSwitch = (step as any)?.data?.waitForTabSwitch
    const scrollToTop = (step as any)?.data?.scrollToTop
    const allowNextWithoutAction = (step as any)?.data?.allowNextWithoutAction
    
    // AGC: Scroll to top like navigation tour
    if (scrollToTop) {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
    
    // AGC: Simulate click on potions tab if requested
    if (simulateClick && clickTarget) {
      setTimeout(() => {
        const potionsTab = document.querySelector(clickTarget) as HTMLElement | null
        if (potionsTab) {
          console.log('AGC: Simulating click on potions tab')
          potionsTab.click()
        } else {
          console.warn('AGC: Potions tab not found for simulation:', clickTarget)
        }
      }, 300) // AGC: Small delay to ensure element is rendered
    }
    
    // AGC: If allowNextWithoutAction is true, don't block - just auto-advance if tab is clicked
    if (waitForTabSwitch && !allowNextWithoutAction) {
      // AGC: Check if potions tab is active
      const checkTabSwitch = setInterval(() => {
        const potionsTab = document.querySelector('[data-tour="battle-potions-tab"]')
        const isActive = potionsTab?.classList.contains('border-yellow-400') || 
                        potionsTab?.classList.contains('border-cyan-400') ||
                        potionsTab?.getAttribute('aria-selected') === 'true' ||
                        document.querySelector('[data-tour="battle-potions-area"]')?.getAttribute('style')?.includes('display: block')
        
        if (isActive || document.querySelector('[data-tour="battle-potions-area"]:not([style*="display: none"])')) {
          console.log('AGC: Potions tab clicked, advancing tour...')
          clearInterval(checkTabSwitch)
          setTimeout(() => {
            nextStep()
          }, 500)
        }
      }, 500)

    return () => {
        clearInterval(checkTabSwitch)
      }
    }
    // AGC: If allowNextWithoutAction is true, user can click Next manually without clicking the tab
  }, [isRunning, activeTourId, currentStepIndex, steps, nextStep])

  // AGC: Handle step 19 (index 18) - Battle Tour: Potion selection
  // AGC: spotlightClicks={true} allows potion clicks without closing tour
  // AGC: This step does NOT auto-advance - user must click "Next" to proceed
  useEffect(() => {
    if (!isRunning || activeTourId !== 'battle' || currentStepIndex !== 18) return

    console.log('AGC: Step 19 (index 18) - Potion selection. User can select a potion and click Next to proceed.')
    
    // AGC: Ensure potions area is visible (step 18 should have clicked it, but double-check)
    // AGC: The potions area is conditionally rendered, not hidden via CSS
    const ensurePotionsVisible = () => {
      const potionsArea = document.querySelector('[data-tour="battle-potions-area"]')
      const potionsTab = document.querySelector('[data-tour="battle-potions-tab"]')
      
      // AGC: If potions area doesn't exist in DOM, click the tab to show it
      if (!potionsArea && potionsTab) {
        console.log('AGC: Potions area not in DOM, clicking potions tab')
        ;(potionsTab as HTMLElement)?.click()
      } else if (potionsArea) {
        console.log('AGC: Potions area is visible in DOM')
      }
    }
    
    // AGC: Try to ensure potions area is visible
    setTimeout(ensurePotionsVisible, 200)
    
    // AGC: Do NOT auto-advance - let user select potion and click Next manually
    // AGC: This prevents timing issues when advancing to step 20 (start button)
  }, [isRunning, activeTourId, currentStepIndex])

  // AGC: Track if step 20 advancement is in progress to prevent double calls
  const step20AdvancingRef = React.useRef(false)
  const step20RetryIntervalRef = React.useRef<NodeJS.Timeout | null>(null)

  // AGC: Handle step 20 (index 19) - Battle Tour: Wait for start button to be clicked
  // AGC: When start button is clicked, advance the tour (same as clicking Next)
  useEffect(() => {
    if (!isRunning || activeTourId !== 'battle' || currentStepIndex !== 19) {
      // AGC: Clean up when leaving step 20
      step20AdvancingRef.current = false
      if (step20RetryIntervalRef.current) {
        clearInterval(step20RetryIntervalRef.current)
        step20RetryIntervalRef.current = null
      }
      return
    }

    // AGC: Reset flags when entering step 20
    step20AdvancingRef.current = false
    if (step20RetryIntervalRef.current) {
      clearInterval(step20RetryIntervalRef.current)
      step20RetryIntervalRef.current = null
    }
    
    console.log('AGC: Step 20 (index 19) - Waiting for start button click.')
    
    // AGC: Single function to advance tour - called from button click or Next
    const advanceToStep21 = () => {
      // AGC: Prevent double calls
      if (step20AdvancingRef.current) {
        console.log('AGC: Step 20 advance already in progress, ignoring...')
        return
      }
      step20AdvancingRef.current = true
      
      // AGC: Clear any existing retry interval
      if (step20RetryIntervalRef.current) {
        clearInterval(step20RetryIntervalRef.current)
        step20RetryIntervalRef.current = null
      }
      
      console.log('AGC: Starting battle and waiting for battle UI...')
      
      let retryCount = 0
      const maxRetries = 40 // AGC: 40 retries = 20 seconds max wait
      
      // AGC: Use interval instead of recursive setTimeout for easier cleanup
      step20RetryIntervalRef.current = setInterval(() => {
        // AGC: Check for step 21's target element specifically
        const turnTimer = document.querySelector('[data-tour="simulation-turn-timer"]')
        
        if (turnTimer) {
          console.log('AGC: Battle UI ready (turn timer found) - waiting 500ms for React to stabilize...')
          if (step20RetryIntervalRef.current) {
            clearInterval(step20RetryIntervalRef.current)
            step20RetryIntervalRef.current = null
          }
          // AGC: Wait a bit for React to finish rendering before advancing
          setTimeout(() => {
            console.log('AGC: Advancing to step 21 now')
            nextStep()
          }, 500)
        } else {
          retryCount++
          if (retryCount >= maxRetries) {
            console.warn('AGC: Battle UI timeout after', retryCount, 'retries - advancing anyway')
            if (step20RetryIntervalRef.current) {
              clearInterval(step20RetryIntervalRef.current)
              step20RetryIntervalRef.current = null
            }
            // AGC: Wait before advancing even on timeout
            setTimeout(() => nextStep(), 500)
          } else if (retryCount % 4 === 0) {
            // AGC: Log every 2 seconds
            console.log(`AGC: Waiting for battle UI... (${retryCount}/${maxRetries})`)
          }
        }
      }, 500)
    }
    
    // AGC: Listen for start button click - use bubbling phase so the button's own handler runs first
    const startButton = document.querySelector('[data-tour="simulation-start-button"]') as HTMLButtonElement | null
    
    const handleStartButtonClick = () => {
      console.log('AGC: Start Simulation button clicked - starting battle and waiting for UI')
      // AGC: Don't prevent default or stop propagation - let the button work normally
      // AGC: Just start waiting for the battle UI to appear
      advanceToStep21()
    }
    
    if (startButton) {
      // AGC: Use bubbling phase (false) so button's own click handler runs first
      startButton.addEventListener('click', handleStartButtonClick, false)
    }
    
    // AGC: Listen for custom event from Next button click (TourTooltip)
    const handleNextFromTooltip = () => {
      console.log('AGC: Next clicked on step 20')
      
      // AGC: Check if battle already started
      const turnTimer = document.querySelector('[data-tour="simulation-turn-timer"]')
      if (turnTimer) {
        console.log('AGC: Battle already started - advancing after short delay')
        if (!step20AdvancingRef.current) {
          step20AdvancingRef.current = true
          // AGC: Wait for React to stabilize before advancing
          setTimeout(() => nextStep(), 300)
        }
        return
      }
      
      // AGC: Try to click the start button to start the battle
      const btn = document.querySelector('[data-tour="simulation-start-button"]') as HTMLButtonElement | null
      if (btn) {
        console.log('AGC: Clicking start button via Next')
        // AGC: Remove our listener first so we don't double-trigger advanceToStep21
        if (startButton) {
          startButton.removeEventListener('click', handleStartButtonClick, false)
        }
        // AGC: Click the button to start the battle
        btn.click()
        // AGC: Start waiting for battle UI
        advanceToStep21()
      } else {
        console.warn('AGC: Start button not found - starting wait anyway')
        advanceToStep21()
      }
    }
    window.addEventListener('tour:step20-next', handleNextFromTooltip)
    
    return () => {
      if (startButton) {
        startButton.removeEventListener('click', handleStartButtonClick, false)
      }
      window.removeEventListener('tour:step20-next', handleNextFromTooltip)
      if (step20RetryIntervalRef.current) {
        clearInterval(step20RetryIntervalRef.current)
        step20RetryIntervalRef.current = null
      }
    }
  }, [isRunning, activeTourId, currentStepIndex, nextStep])

  // AGC: Handle step 21 (index 20) - Battle Tour: Highlight turn/timer after battle starts
  useEffect(() => {
    if (!isRunning || activeTourId !== 'battle' || currentStepIndex !== 20) return

    console.log('AGC: Step 21 (index 20) - Battle turn/timer step')
    
    // AGC: Highlight the turn/timer element when it appears
    let retryCount = 0
    const maxRetries = 20
    
    const highlightTurnTimer = () => {
      const turnTimer = document.querySelector('[data-tour="simulation-turn-timer"]') as HTMLElement | null
      if (turnTimer) {
        console.log('AGC: Turn/timer found, highlighting...')
        turnTimer.style.position = 'relative'
        turnTimer.style.zIndex = '10001'
        turnTimer.style.boxShadow = '0 0 0 4px rgba(234, 179, 8, 1), 0 0 40px rgba(234, 179, 8, 0.8)'
        turnTimer.style.borderRadius = '16px'
        turnTimer.style.animation = 'yellowFlashSpotlight 1.5s ease-in-out infinite'
      } else {
        retryCount++
        if (retryCount < maxRetries) {
          console.log(`AGC: Turn/timer not found, retrying... (${retryCount}/${maxRetries})`)
          setTimeout(highlightTurnTimer, 500)
        }
      }
    }
    
    // AGC: Start checking immediately
    const timer = setTimeout(highlightTurnTimer, 200)
    
    return () => {
      clearTimeout(timer)
      const turnTimer = document.querySelector('[data-tour="simulation-turn-timer"]') as HTMLElement | null
      if (turnTimer) {
        turnTimer.style.position = ''
        turnTimer.style.zIndex = ''
        turnTimer.style.boxShadow = ''
        turnTimer.style.borderRadius = ''
        turnTimer.style.animation = ''
      }
    }
  }, [isRunning, activeTourId, currentStepIndex, steps])

  // AGC: Handle step 21 (index 21) - Battle Tour: Highlight player energy area
  useEffect(() => {
    if (!isRunning || activeTourId !== 'battle' || currentStepIndex !== 21) return

    console.log('AGC: Step 22 (index 22) - Highlighting player energy area')
    
    // AGC: Highlight player energy area
    const highlightPlayerEnergy = () => {
      const playerEnergy = document.querySelector('[data-tour="simulation-player-energy"]') as HTMLElement | null
      if (playerEnergy) {
        console.log('AGC: Player energy area found, highlighting...')
        playerEnergy.style.position = 'relative'
        playerEnergy.style.zIndex = '10001'
        playerEnergy.style.boxShadow = '0 0 0 4px rgba(234, 179, 8, 1), 0 0 40px rgba(234, 179, 8, 0.8)'
        playerEnergy.style.borderRadius = '16px'
        playerEnergy.style.animation = 'yellowFlashSpotlight 1.5s ease-in-out infinite'
      } else {
        console.log('AGC: Player energy area not found yet, retrying...')
        setTimeout(highlightPlayerEnergy, 500)
      }
    }
    
    // AGC: Start checking after a delay
    const timer = setTimeout(highlightPlayerEnergy, 500)
    
    return () => {
      clearTimeout(timer)
      const playerEnergy = document.querySelector('[data-tour="simulation-player-energy"]') as HTMLElement | null
      if (playerEnergy) {
        playerEnergy.style.position = ''
        playerEnergy.style.zIndex = ''
        playerEnergy.style.boxShadow = ''
        playerEnergy.style.borderRadius = ''
        playerEnergy.style.animation = ''
      }
    }
  }, [isRunning, activeTourId, currentStepIndex])

  // AGC: Handle step 26 (index 25) - Battle Tour: Submit Turn - advance on click or Next button
  useEffect(() => {
    if (!isRunning || activeTourId !== 'battle' || currentStepIndex !== 25) return

    console.log('AGC: Step 26 (index 25) - Submit Turn. Clicking button or Next will advance tour.')
    
    const submitButton = document.querySelector('[data-tour="simulation-submit-turn"]') as HTMLElement | null
    
    const handleSubmitClick = () => {
      console.log('AGC: Submit Turn clicked - advancing tour')
      setTimeout(() => {
        nextStep()
      }, 500)
    }
    
    // AGC: Listen for custom event from Next button click (same behavior as submit button)
    const handleNextFromTooltip = () => {
      console.log('AGC: Next clicked on step 26 - clicking submit button')
      if (submitButton) {
        submitButton.click()
      } else {
        // AGC: Fallback - advance directly
        setTimeout(() => nextStep(), 500)
      }
    }
    
    if (submitButton) {
      submitButton.addEventListener('click', handleSubmitClick, true)
    }
    window.addEventListener('tour:step26-next', handleNextFromTooltip)
    
    return () => {
      if (submitButton) {
        submitButton.removeEventListener('click', handleSubmitClick, true)
      }
      window.removeEventListener('tour:step26-next', handleNextFromTooltip)
    }
  }, [isRunning, activeTourId, currentStepIndex, nextStep])

  // AGC: Handle step 27 (index 26) - Battle Tour: Your Card (from BattleTurnModal) - advance on click
  useEffect(() => {
    if (!isRunning || activeTourId !== 'battle' || currentStepIndex !== 26) return

    console.log('AGC: Step 27 (index 26) - Your Card from BattleTurnModal. Clicking will advance tour.')
    
    // AGC: Target the full YOU card from BattleTurnModal
    const yourCard = document.querySelector('[data-tour="simulation-your-card-full"]') as HTMLElement | null
    
    const handleCardClick = (e: Event) => {
      e.stopPropagation()
      console.log('AGC: Your Card clicked - advancing tour')
      setTimeout(() => {
        nextStep()
      }, 300)
    }
    
    if (yourCard) {
      yourCard.addEventListener('click', handleCardClick, true)
    }
    
    return () => {
      if (yourCard) {
        yourCard.removeEventListener('click', handleCardClick, true)
      }
    }
  }, [isRunning, activeTourId, currentStepIndex, nextStep])

  // AGC: Handle step 28 (index 27) - Battle Tour: Scroll and highlight opponent card
  useEffect(() => {
    if (!isRunning || activeTourId !== 'battle' || currentStepIndex !== 27) return

    console.log('AGC: Step 28 (index 27) - Scrolling to opponent card and highlighting')
    
    // AGC: Highlight the opponent card
    const opponentCard = document.querySelector('[data-tour="simulation-opponent-card-full"]') as HTMLElement | null
    
    if (opponentCard) {
      // AGC: First scroll the modal container to show opponent card near top
      const modalContainer = opponentCard.closest('.overflow-y-auto') as HTMLElement | null
      if (modalContainer) {
        // AGC: Calculate position to scroll opponent card to top with some padding for tooltip
        const cardRect = opponentCard.getBoundingClientRect()
        const containerRect = modalContainer.getBoundingClientRect()
        const scrollOffset = cardRect.top - containerRect.top + modalContainer.scrollTop - 150 // 150px padding for tooltip
        modalContainer.scrollTo({ top: Math.max(0, scrollOffset), behavior: 'smooth' })
      }
      
      // AGC: Apply highlight after a short delay to let scroll complete
      setTimeout(() => {
        opponentCard.style.position = 'relative'
        opponentCard.style.zIndex = '10001'
        opponentCard.style.boxShadow = '0 0 0 4px rgba(234, 179, 8, 1), 0 0 40px rgba(234, 179, 8, 0.8)'
        opponentCard.style.borderRadius = '16px'
        opponentCard.style.animation = 'yellowFlashSpotlight 1.5s ease-in-out infinite'
      }, 300)
    }
    
    return () => {
      if (opponentCard) {
        opponentCard.style.position = ''
        opponentCard.style.zIndex = ''
        opponentCard.style.boxShadow = ''
        opponentCard.style.borderRadius = ''
        opponentCard.style.animation = ''
      }
    }
  }, [isRunning, activeTourId, currentStepIndex])

  // AGC: Handle step 30 (index 29) - Battle Tour: Create cutouts around close and continue buttons
  useEffect(() => {
    if (!isRunning || activeTourId !== 'battle' || currentStepIndex !== 29) return

    console.log('AGC: Step 30 (index 29) - Creating cutouts around close and continue buttons')
    
    const closeButton = document.querySelector('[data-tour="simulation-close-button"]') as HTMLElement | null
    const continueButton = document.querySelector('[data-tour="simulation-continue-button"]') as HTMLElement | null
    
    // AGC: Apply highlight style and create visual cutout effect for buttons
    const highlightStyle = {
      position: 'relative' as const,
      zIndex: '10002',
      boxShadow: '0 0 0 4px rgba(234, 179, 8, 1), 0 0 40px rgba(234, 179, 8, 0.8)',
      borderRadius: '16px',
      animation: 'yellowFlashSpotlight 1.5s ease-in-out infinite',
    }
    
    // AGC: Create a custom overlay with cutouts for both buttons
    const createCutoutOverlay = () => {
      const overlay = document.createElement('div')
      overlay.id = 'tour-dual-cutout-overlay'
      overlay.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100vh;
        z-index: 10000;
        pointer-events: none;
        background: transparent;
      `
      
      // AGC: Create cutout highlights for close button
      if (closeButton) {
        const closeRect = closeButton.getBoundingClientRect()
        const closeCutout = document.createElement('div')
        closeCutout.id = 'tour-cutout-close'
        closeCutout.style.cssText = `
          position: fixed;
          top: ${closeRect.top - 8}px;
          left: ${closeRect.left - 8}px;
          width: ${closeRect.width + 16}px;
          height: ${closeRect.height + 16}px;
          border-radius: 16px;
          box-shadow: 0 0 0 4px rgba(234, 179, 8, 1), 0 0 40px rgba(234, 179, 8, 0.8);
          z-index: 10001;
          pointer-events: none;
          animation: yellowFlashSpotlight 1.5s ease-in-out infinite;
        `
        overlay.appendChild(closeCutout)
        Object.assign(closeButton.style, highlightStyle)
      }
      
      // AGC: Create cutout highlights for continue button
      if (continueButton) {
        const continueRect = continueButton.getBoundingClientRect()
        const continueCutout = document.createElement('div')
        continueCutout.id = 'tour-cutout-continue'
        continueCutout.style.cssText = `
          position: fixed;
          top: ${continueRect.top - 8}px;
          left: ${continueRect.left - 8}px;
          width: ${continueRect.width + 16}px;
          height: ${continueRect.height + 16}px;
          border-radius: 16px;
          box-shadow: 0 0 0 4px rgba(234, 179, 8, 1), 0 0 40px rgba(234, 179, 8, 0.8);
          z-index: 10001;
          pointer-events: none;
          animation: yellowFlashSpotlight 1.5s ease-in-out infinite;
        `
        overlay.appendChild(continueCutout)
        Object.assign(continueButton.style, highlightStyle)
      }
      
      document.body.appendChild(overlay)
    }
    
    // AGC: Create the cutout overlay
    createCutoutOverlay()
    
    return () => {
      // AGC: Remove cutout overlay
      document.getElementById('tour-dual-cutout-overlay')?.remove()
      
      if (closeButton) {
        closeButton.style.position = ''
        closeButton.style.zIndex = ''
        closeButton.style.boxShadow = ''
        closeButton.style.borderRadius = ''
        closeButton.style.animation = ''
      }
      if (continueButton) {
        continueButton.style.position = ''
        continueButton.style.zIndex = ''
        continueButton.style.boxShadow = ''
        continueButton.style.borderRadius = ''
        continueButton.style.animation = ''
      }
    }
  }, [isRunning, activeTourId, currentStepIndex])

  // AGC: Don't render if no tour is running
  if (!isRunning || !activeTourId || steps.length === 0) {
    return null
  }

  return (
    <>
      {/* AGC: Battle Mode Selection Modal (step 13) */}
      <BattleModeSelectionModal
        isOpen={showModeSelection}
        onSelectPvE={() => {
          setShowModeSelection(false)
          // AGC: Dispatch event to start PvE battle
          window.dispatchEvent(new CustomEvent('tour:select-pve-mode'))
          // AGC: Auto-advance to step 14 after selection
          setTimeout(() => {
            nextStep()
          }, 500)
        }}
        onSelectSimulation={() => {
          setShowModeSelection(false)
          // AGC: Dispatch event to start simulation
          window.dispatchEvent(new CustomEvent('tour:select-simulation-mode'))
          // AGC: Auto-advance to step 14 after selection
          setTimeout(() => {
            nextStep()
          }, 500)
        }}
      />
      
      {/* AGC: CSS for yellow flashing animation on spotlight (target element), not tooltip */}
      <style>{`
        @keyframes yellowFlashSpotlight {
          0%, 100% {
            box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.85), 
                        0 0 0 4px rgba(234, 179, 8, 1), 
                        0 0 40px rgba(234, 179, 8, 0.8),
                        0 0 80px rgba(234, 179, 8, 0.4),
                        inset 0 0 0 3px rgba(234, 179, 8, 1),
                        inset 0 0 20px rgba(234, 179, 8, 0.6) !important;
          }
          50% {
            box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.85), 
                        0 0 0 8px rgba(234, 179, 8, 1), 
                        0 0 60px rgba(234, 179, 8, 1),
                        0 0 120px rgba(234, 179, 8, 0.6),
                        inset 0 0 0 4px rgba(234, 179, 8, 1),
                        inset 0 0 30px rgba(234, 179, 8, 0.8) !important;
          }
        }
        
        /* AGC: Apply flashing animation ONLY to react-joyride spotlight (the highlighted area around target) */
        /* AGC: Use very specific selector to target ONLY the spotlight div */
        /* AGC: Inner border glow added with inset box-shadow */
        div.react-joyride__spotlight,
        div[class="react-joyride__spotlight"] {
          animation: yellowFlashSpotlight 1.5s ease-in-out infinite !important;
          box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.85), 
                     0 0 0 4px rgba(234, 179, 8, 1), 
                     0 0 40px rgba(234, 179, 8, 0.8),
                     0 0 80px rgba(234, 179, 8, 0.4),
                     inset 0 0 0 3px rgba(234, 179, 8, 1),
                     inset 0 0 20px rgba(234, 179, 8, 0.6) !important;
        }
        
        /* AGC: CRITICAL - Explicitly remove ALL animations and yellow box-shadows from tooltip and all its children */
        div.react-joyride__tooltip,
        div[class*="react-joyride__tooltip"],
        div[class*="react-joyride__tooltip"] *,
        div[class*="react-joyride__beacon"],
        div[class*="react-joyride__floater"],
        [data-joyride="tooltip"],
        [data-joyride="beacon"],
        [class*="floater"],
        [class*="tooltip"]:not([class*="spotlight"]) {
          animation: none !important;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06) !important;
        }
        
        /* AGC: Hide overlay for step 5 (battle tour, index 4) - no dimming, full page visible */
        div.react-joyride__overlay {
          /* AGC: Overlay will be hidden via JavaScript for step 5 */
        }
        
        /* AGC: Ensure tooltip container has NO yellow glow effects - use neutral shadow only */
        .react-joyride__tooltip,
        [class*="react-joyride__tooltip"] {
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06) !important;
          animation: none !important;
        }
        
        /* AGC: Remove any yellow colors/glows from tooltip children */
        .react-joyride__tooltip *,
        [class*="react-joyride__tooltip"] * {
          box-shadow: none !important;
          animation: none !important;
        }
        
        /* AGC: Prevent any rgba(234, 179, 8) yellow colors in tooltip box-shadows */
        [class*="tooltip"]:not([class*="spotlight"]) {
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06) !important;
        }
      `}</style>
      
      <Joyride
        callback={handleJoyrideCallback}
        continuous
        disableCloseOnEsc={false}
        disableOverlayClose={true} // AGC: Prevent clicking overlay from closing tour (allows buff selection on step 17)
        disableScrolling={false}
        hideCloseButton={false}
        run={isRunning}
        scrollToFirstStep
        showProgress={false}
        showSkipButton={true}
        spotlightClicks={true} // AGC: Allow clicks inside spotlight area (enables buff/potion selection)
        spotlightPadding={12}
        stepIndex={currentStepIndex}
        steps={steps}
        styles={joyrideStyles}
        tooltipComponent={renderTooltip}
        floaterProps={{
          disableAnimation: false,
          styles: {
            floater: {
              filter: 'drop-shadow(0 4px 6px rgba(0, 0, 0, 0.1))', // AGC: Neutral shadow, no yellow
            },
          },
        }}
      />
    </>
  )
}

export default TourOverlay

